import {
  Input,
  Modal,
  Textarea,
  UnstyledButton,
  type InputProps,
  type ModalProps,
  type TextareaProps,
  type UnstyledButtonProps,
} from "@mantine/core";
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import classes from "./ui.module.css";

/**
 * Facet owns the visual language while Mantine owns interaction semantics.
 * Screens consume this boundary instead of accumulating framework-specific
 * configuration or falling back to handwritten interactive primitives.
 */
export const FacetButton = forwardRef<HTMLButtonElement, UnstyledButtonProps & ComponentPropsWithoutRef<"button">>(
  function FacetButton(props, ref) {
    return <UnstyledButton ref={ref} {...props} type={props.type ?? "button"} />;
  },
);

export const FacetInput = forwardRef<HTMLInputElement, InputProps & ComponentPropsWithoutRef<"input">>(
  function FacetInput(props, ref) {
    return <Input ref={ref} unstyled {...props} />;
  },
);

export const FacetTextarea = forwardRef<HTMLTextAreaElement, TextareaProps & ComponentPropsWithoutRef<"textarea">>(
  function FacetTextarea(props, ref) {
    return <Textarea ref={ref} unstyled {...props} />;
  },
);

export const FacetSelect = forwardRef<HTMLSelectElement, InputProps & ComponentPropsWithoutRef<"select">>(
  function FacetSelect(props, ref) {
    return <Input component="select" ref={ref} unstyled {...props} />;
  },
);

type FacetModalProps = Omit<ModalProps, "children" | "classNames" | "withCloseButton"> & {
  children: ReactNode;
  contentClassName?: string;
};

export function FacetModal({ children, contentClassName = "", ...props }: FacetModalProps) {
  return (
    <Modal
      {...props}
      withCloseButton={false}
      classNames={{ content: `${classes.modalContent} ${contentClassName}`.trim(), body: classes.modalBody }}
    >
      {children}
    </Modal>
  );
}
